import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Bell, Home, Repeat, Layers, Heart, User, Menu, LogOut, Shield, Settings as Cog, LayoutDashboard, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LiveBanner } from "@/components/LiveBanner";

const PUBLIC_NAV = [
  { to: "/scambi", label: "Scambi" },
  { to: "/community", label: "Community" },
  { to: "/come-funziona", label: "Come funziona" },
];

const BOTTOM_NAV = [
  { to: "/", label: "Home", icon: Home },
  { to: "/scambi", label: "Scambi", icon: Repeat },
  { to: "/le-mie-carte", label: "Mie Carte", icon: Layers, auth: true },
  { to: "/wishlist", label: "Wishlist", icon: Heart, auth: true },
  { to: "/dashboard", label: "Profilo", icon: User, auth: true },
];

function NotificationsBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ unread: 0, items: [] });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user) { load(); const t = setInterval(load, 30000); return () => clearInterval(t); } }, [user]);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-lg p-2 text-[#CBD5E1] hover:bg-[#1C2640] focus-visible:ring-2 focus-visible:ring-[#2DD4BF]">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#7C3AED] px-1 text-[10px] font-bold text-white">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifiche</DropdownMenuLabel>
          {data.unread > 0 && <button onClick={markAll} data-testid="notifications-read-all" className="text-xs text-[#2DD4BF] hover:underline">Segna lette</button>}
        </div>
        <DropdownMenuSeparator className="bg-[#2A3655]" />
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="px-3 py-6 text-center text-sm text-[#94A3B8]">Nessuna notifica</p>}
          {data.items.slice(0, 12).map((n) => (
            <div key={n.id} data-testid="notification-item" className={`px-3 py-2 text-sm ${n.read ? "text-[#94A3B8]" : "text-[#F8FAFC]"}`}>
              <span className={`mr-2 inline-block h-1.5 w-1.5 rounded-full ${n.read ? "bg-transparent" : "bg-[#2DD4BF]"}`} />
              {n.message}
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#0B1020] pb-16 md:pb-0">
      <LiveBanner />
      <header className="sticky top-0 z-50 glass border-b border-[#2A3655]/70">
        <div className="mx-auto flex h-20 max-w-container items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="nav-logo"><Logo size={56} /></Link>
            <nav className="hidden items-center gap-1 md:flex">
              {PUBLIC_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#2DD4BF]" : "text-[#CBD5E1] hover:text-[#F8FAFC]"}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-2 rounded-lg border border-[#2A3655] px-2.5 py-1.5 text-sm text-[#F8FAFC] hover:bg-[#1C2640]">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-[#7C3AED] text-xs font-bold">{user.username?.[0]?.toUpperCase()}</span>
                    <span className="hidden max-w-24 truncate sm:inline">{user.username}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
                  <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/match" data-testid="menu-matches"><Handshake className="mr-2 h-4 w-4" />Match</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/proposte" data-testid="menu-proposals"><Repeat className="mr-2 h-4 w-4" />Le mie trattative</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/impostazioni" data-testid="menu-settings"><Cog className="mr-2 h-4 w-4" />Impostazioni</Link></DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><Shield className="mr-2 h-4 w-4" />Amministrazione</Link></DropdownMenuItem>}
                  <DropdownMenuSeparator className="bg-[#2A3655]" />
                  <DropdownMenuItem onClick={doLogout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Esci</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden items-center gap-2 sm:flex">
                <Button asChild variant="ghost" className="text-[#CBD5E1] hover:text-[#F8FAFC]"><Link to="/accedi" data-testid="nav-login">Accedi</Link></Button>
                <Button asChild className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Link to="/registrati" data-testid="nav-register">Registrati</Link></Button>
              </div>
            )}
            {!user && (
              <Sheet>
                <SheetTrigger asChild><button data-testid="mobile-menu" className="rounded-lg p-2 text-[#CBD5E1] sm:hidden"><Menu className="h-5 w-5" /></button></SheetTrigger>
                <SheetContent side="right" className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">
                  <div className="mt-8 flex flex-col gap-2">
                    {PUBLIC_NAV.map((n) => <Link key={n.to} to={n.to} className="rounded-lg px-3 py-2 hover:bg-[#1C2640]">{n.label}</Link>)}
                    <Link to="/accedi" className="rounded-lg px-3 py-2 hover:bg-[#1C2640]">Accedi</Link>
                    <Link to="/registrati" className="rounded-lg bg-[#7C3AED] px-3 py-2 text-center">Registrati</Link>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-container px-4 py-6 sm:px-6 sm:py-10">{children}</main>

      <Footer />

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t border-[#2A3655] bg-[#141B2D]/95 backdrop-blur-md md:hidden" data-testid="mobile-bottom-nav">
        {BOTTOM_NAV.map((n) => {
          const Icon = n.icon;
          const to = n.auth && !user ? "/accedi" : n.to;
          const active = loc.pathname === n.to;
          return (
            <Link key={n.label} to={to} data-testid={`bottom-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex flex-col items-center gap-0.5 px-2 text-[10px] ${active ? "text-[#2DD4BF]" : "text-[#94A3B8]"}`}>
              <Icon className="h-5 w-5" />{n.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function Footer() {
  const links = [
    ["/come-funziona", "Come funziona"], ["/community", "Community"],
    ["/regolamento", "Regolamento"], ["/sicurezza", "Sicurezza"],
    ["/assistenza", "Assistenza"], ["/termini", "Termini"],
    ["/privacy", "Privacy"], ["/cookie", "Cookie"],
  ];
  return (
    <footer className="border-t border-[#2A3655] bg-[#0B1020]">
      <div className="mx-auto max-w-container px-4 py-10 sm:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="max-w-sm">
            <Logo size={64} />
            <p className="mt-3 text-sm text-[#94A3B8]">La community gratuita per gli appassionati di carte collezionabili. Scambi carta contro carta, nessuna vendita.</p>
          </div>
          <nav className="grid grid-cols-2 gap-x-10 gap-y-2 sm:grid-cols-4">
            {links.map(([to, label]) => (
              <Link key={to} to={to} data-testid={`footer-${label.toLowerCase().replace(/\s/g, "-")}`} className="text-sm text-[#CBD5E1] hover:text-[#2DD4BF]">{label}</Link>
            ))}
          </nav>
        </div>
        <p className="mt-8 text-xs text-[#64748B]">© {new Date().getFullYear()} Omniverse · Community per maggiorenni · Solo scambi tra collezionisti</p>
      </div>
    </footer>
  );
}
